/**
 * 远程文件传输界面模块(文件传输 v1)。
 *
 * 发送区(随 showFileTransfer 显隐):浏览器文件选择器 → file:offer → chunk(§8)。
 * 接收区(**连接期间常驻**):收到对端 file:offer 后弹确认(§5 / §7.2,显示来源/文件名/大小,
 * 可 Accept/Reject);接受后展示接收进度并可取消;完成后由 session 触发浏览器下载。
 * 接收确认与进度不受 showFileTransfer 控制——否则面板未展开时对端发文件用户看不到确认框。
 * 远端目录浏览 / 拉取(§2)v1 不做,已移除。
 */
import { useEffect, useRef, useState } from "react";
import {
	acceptIncomingOffer,
	cancelFileTransfer,
	type IncomingOffer,
	registerFileTransferUI,
	rejectIncomingOffer,
	sendFile,
} from "@/services/remote/file-transfer";

/**
 * 文件传输主组件。
 */
type FileTransferProps = {
	showFileTransfer?: boolean;
};

type ReceiveProgress = { receivedBytes: number; totalBytes: number };

/**
 * 人类可读的字节展示。
 * @param {number} bytes 字节数
 */
const formatBytes = (bytes: number): string => {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * 文件传输主组件。
 * @param {FileTransferProps} props 组件参数;showFileTransfer 仅控制发送区显隐。
 */
export default function FileTransfer({
	showFileTransfer = false,
}: FileTransferProps) {
	const [sendStatus, setSendStatus] = useState<string>("");
	const [sendProgress, setSendProgress] = useState<{
		sentBytes: number;
		totalBytes: number;
	}>({ sentBytes: 0, totalBytes: 0 });

	const [incomingOffer, setIncomingOffer] = useState<IncomingOffer | null>(
		null,
	);
	const [receiveProgress, setReceiveProgress] = useState<ReceiveProgress>({
		receivedBytes: 0,
		totalBytes: 0,
	});
	const [receiveStatus, setReceiveStatus] = useState<string>("");
	// host(被控端)是否声明可接收文件(§5:控制端只消费 host 能力)。host 付费 gate 未通过 →
	// host hello 不声明能力 → 此处 false → 隐藏发送入口。
	const [hostCanReceive, setHostCanReceive] = useState(false);

	const fileInputRef = useRef<HTMLInputElement | null>(null);

	/**
	 * 注册接收侧 session 回调(挂载),卸载时注销。register 时 session 会回放当前待确认 offer/进度/host 能力。
	 */
	useEffect(() => {
		const unregister = registerFileTransferUI({
			onIncomingOffer: (offer) => setIncomingOffer(offer),
			onReceiveProgress: (p) => setReceiveProgress(p),
			onReceiveStatus: (message) => setReceiveStatus(message),
			onHostCapabilities: (caps) => setHostCanReceive(caps.canReceive),
		});
		return unregister;
	}, []);

	/**
	 * 发送文件到远端。
	 */
	const handleSendFile = () => {
		const file = fileInputRef.current?.files?.[0] || null;
		sendFile(file, {
			onStatus: (message) => setSendStatus(message),
			onError: (message) => setSendStatus(message),
			onProgress: ({ sentBytes, totalBytes }) =>
				setSendProgress({ sentBytes, totalBytes }),
			onComplete: () => setSendStatus("File sent successfully"),
		});
	};

	const isReceiving = receiveProgress.totalBytes > 0 && !incomingOffer;
	const receivePercent =
		receiveProgress.totalBytes > 0
			? (receiveProgress.receivedBytes / receiveProgress.totalBytes) * 100
			: 0;

	return (
		<div className="container mx-auto">
			{/* 文件发送区域:面板展开 + host 声明可接收(§5 付费 gate 由 host 能力体现)时才显示 */}
			{showFileTransfer && !hostCanReceive && (
				<div className="container mx-auto mt-8 text-sm text-gray-500">
					File transfer isn't available for this device — the host's plan or
					settings don't allow receiving files.
				</div>
			)}
			{showFileTransfer && hostCanReceive && (
				<div className="container mx-auto mt-8">
					<span className="title">Send File</span>
					<div>
						<div>
							<input ref={fileInputRef} type="file" />
						</div>

						<div>
							<button
								type="button"
								className="btn btn-outline btn-sm"
								onClick={handleSendFile}
							>
								Send
							</button>
							{sendProgress.totalBytes > 0 &&
								sendProgress.sentBytes < sendProgress.totalBytes && (
									<button
										type="button"
										className="btn btn-outline btn-sm ml-2"
										onClick={cancelFileTransfer}
									>
										Cancel
									</button>
								)}
						</div>
					</div>

					<div className="progress">
						<div className="label">
							<b>Send Progress &nbsp;&nbsp;&nbsp;</b>
						</div>
						<progress
							max={sendProgress.totalBytes || 0}
							value={sendProgress.sentBytes}
						></progress>
					</div>
					{sendProgress.totalBytes > 0 && (
						<div className="text-sm text-gray-600 mt-1">
							{sendProgress.sentBytes.toLocaleString()} /{" "}
							{sendProgress.totalBytes.toLocaleString()} bytes
						</div>
					)}
					{sendStatus && (
						<div className="mt-2 text-sm text-gray-600">{sendStatus}</div>
					)}
				</div>
			)}

			{/* 文件接收区域:接收确认 + 接收进度**始终常驻**(对端发文件必须可见,§5 / §7.2);
			    标题/状态行随面板展开。 */}
			<div className="container mx-auto mt-8">
				{showFileTransfer && <span className="title">Receive File</span>}

				{/* 接收确认对话框:显示来源/文件名/大小 + Accept/Reject */}
				{incomingOffer && (
					<div className="mt-4 rounded border border-gray-300 p-3">
						<div className="text-sm font-medium">Incoming file</div>
						<div className="mt-1 text-sm text-gray-700">
							<div>Name: {incomingOffer.name}</div>
							<div>Size: {formatBytes(incomingOffer.size)}</div>
							<div>Type: {incomingOffer.mime}</div>
						</div>
						<div className="mt-3">
							<button
								type="button"
								className="btn btn-outline btn-sm"
								onClick={acceptIncomingOffer}
							>
								Accept
							</button>
							<button
								type="button"
								className="btn btn-outline btn-sm ml-2"
								onClick={rejectIncomingOffer}
							>
								Reject
							</button>
						</div>
					</div>
				)}

				{/* 接收进度 + 取消 */}
				{isReceiving && (
					<div className="mt-4">
						<div className="flex items-center justify-between mb-2">
							<span className="text-sm font-medium">Receiving…</span>
							<button
								type="button"
								className="btn btn-outline btn-xs"
								onClick={cancelFileTransfer}
							>
								Cancel
							</button>
						</div>

						<div className="progress">
							<div className="label">
								<b>Receive Progress &nbsp;&nbsp;&nbsp;</b>
							</div>
							<progress
								max={receiveProgress.totalBytes}
								value={receiveProgress.receivedBytes}
							></progress>
						</div>

						<div className="text-sm text-gray-600 mt-1">
							{receiveProgress.receivedBytes.toLocaleString()} /{" "}
							{receiveProgress.totalBytes.toLocaleString()} bytes (
							{receivePercent.toFixed(1)}%)
						</div>
					</div>
				)}

				{showFileTransfer && receiveStatus && (
					<div className="mt-2 text-sm text-gray-600">{receiveStatus}</div>
				)}
			</div>
		</div>
	);
}
